import { AtwProductMark } from "@/components/Brand/AtwProductMark";
import {
  atwMarkIdForAppSlug,
  atwMarkIdForSystem,
} from "@/components/Brand/atwProductMarks";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DASHBOARD_APP_BY_SLUG, type GlobalAppSlug } from "@/lib/dashboardAppRegistry";
import { getEffectiveDashboardAppSlugs } from "@/lib/dashboardAppAccess";
import {
  ATW_SYSTEMS,
  atwSystemLaunchUrl,
  effectiveStoredSystemAccess,
  hasWorkspaceSystemAccess,
  type AtwStoredSystemId,
  type AtwSystemDefinition,
} from "@/lib/atwSystemAccess";

const markToneClass = "dark:brightness-0 dark:invert";

export type SystemAccessDraft = {
  systemAccess: AtwStoredSystemId[];
  /** Explicit app entitlements (no `home`). */
  apps: Exclude<GlobalAppSlug, "home">[];
};

export function buildSystemAccessDraft(input: {
  role: string;
  appEntitlements: GlobalAppSlug[] | null | undefined;
  systemAccess: AtwStoredSystemId[] | null | undefined;
  orgId: string | null | undefined;
  orgMailEnabled: boolean;
}): SystemAccessDraft {
  const apps = getEffectiveDashboardAppSlugs(input.role, input.appEntitlements, input.orgId).filter(
    (s): s is Exclude<GlobalAppSlug, "home"> => s !== "home",
  );
  return {
    systemAccess: effectiveStoredSystemAccess(input.systemAccess, {
      orgMailEnabled: input.orgMailEnabled,
    }),
    apps,
  };
}

export function roleHasAllApps(role: string): boolean {
  return role === "account_owner" || role === "super_admin";
}

type Props = {
  value: SystemAccessDraft;
  onChange?: (next: SystemAccessDraft) => void;
  /** Apps this tenant may grant (Platform only for the ATW org + super admin). */
  assignableAppSlugs: Exclude<GlobalAppSlug, "home">[];
  /** Owners / super admins always have every app; system toggles still apply. */
  appsLocked?: boolean;
  readOnly?: boolean;
};

function systemEnabled(system: AtwSystemDefinition, value: SystemAccessDraft): boolean {
  if (system.storage.kind === "systemAccess") {
    const id = system.id as AtwStoredSystemId;
    if (id === "workspaces") return hasWorkspaceSystemAccess(value.systemAccess);
    return value.systemAccess.includes(id);
  }
  return value.apps.includes(system.storage.slug as Exclude<GlobalAppSlug, "home">);
}

export function visibleSystems(assignableAppSlugs: Exclude<GlobalAppSlug, "home">[]): AtwSystemDefinition[] {
  const assignable = new Set<string>(assignableAppSlugs);
  return ATW_SYSTEMS.filter(
    (s) => s.storage.kind === "systemAccess" || assignable.has(s.storage.slug),
  );
}

export function SystemAccessPanel({ value, onChange, assignableAppSlugs, appsLocked, readOnly }: Props) {
  const assignable = new Set<string>(assignableAppSlugs);
  const editable = !readOnly && Boolean(onChange);

  const setSystem = (system: AtwSystemDefinition, on: boolean) => {
    if (!onChange) return;
    if (system.storage.kind === "systemAccess") {
      const id = system.id as AtwStoredSystemId;
      const next = new Set(value.systemAccess);
      if (on) next.add(id);
      else next.delete(id);
      onChange({ ...value, systemAccess: [...next] });
      return;
    }
    const slug = system.storage.slug as Exclude<GlobalAppSlug, "home">;
    const next = new Set(value.apps);
    if (on) next.add(slug);
    else next.delete(slug);
    onChange({ ...value, apps: [...next] });
  };

  const setApp = (slug: Exclude<GlobalAppSlug, "home">, on: boolean) => {
    if (!onChange) return;
    const next = new Set(value.apps);
    if (on) next.add(slug);
    else next.delete(slug);
    onChange({ ...value, apps: [...next] });
  };

  return (
    <ul className="space-y-3">
      {visibleSystems(assignableAppSlugs).map((system) => {
        const on = systemEnabled(system, value);
        const systemToggleLocked = appsLocked && system.storage.kind === "appEntitlement";
        const innerApps = (system.innerAppSlugs || []).filter((s) => assignable.has(s));
        const launchUrl = on && readOnly ? atwSystemLaunchUrl(system.id) : null;
        return (
          <li
            key={system.id}
            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-[#1D232D]"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 items-start gap-3">
                {atwMarkIdForSystem(system.id) ? (
                  <AtwProductMark
                    id={atwMarkIdForSystem(system.id)!}
                    size={36}
                    className={markToneClass}
                    label
                  />
                ) : null}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {launchUrl ? (
                      <a
                        href={launchUrl}
                        className="font-medium text-gray-900 underline-offset-2 hover:underline dark:text-white"
                      >
                        {system.name}
                      </a>
                    ) : (
                      <p className="font-medium text-gray-900 dark:text-white">{system.name}</p>
                    )}
                    {system.publicHost ? (
                      launchUrl ? (
                        <a
                          href={launchUrl}
                          className="text-xs text-gray-500 underline-offset-2 hover:underline dark:text-gray-400"
                        >
                          {system.publicHost}
                        </a>
                      ) : (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {system.publicHost}
                        </span>
                      )
                    ) : null}
                  </div>
                  <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{system.summary}</p>
                </div>
              </div>
              {editable && !systemToggleLocked ? (
                <Switch
                  aria-label={`${system.name} access`}
                  checked={on}
                  onCheckedChange={(checked) => setSystem(system, checked === true)}
                />
              ) : (
                <Badge variant={on ? "default" : "outline"}>{on ? "Can open" : "No access"}</Badge>
              )}
            </div>

            {on && innerApps.length > 0 ? (
              <div className="mt-3 border-t border-gray-100 pt-3 dark:border-white/10">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Inside {system.name}
                </p>
                {appsLocked ? (
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Organization owners and super admins can use every app.
                  </p>
                ) : (
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {innerApps.map((slug) => {
                      const appOn = value.apps.includes(slug);
                      const appMark = atwMarkIdForAppSlug(slug);
                      return (
                        <li key={slug} className="flex items-center justify-between gap-3">
                          <Label
                            htmlFor={`dir-app-${system.id}-${slug}`}
                            className="flex items-center gap-2 text-sm font-normal"
                          >
                            {appMark ? (
                              <AtwProductMark
                                id={appMark}
                                size={22}
                                className={markToneClass}
                              />
                            ) : null}
                            {DASHBOARD_APP_BY_SLUG[slug].label}
                          </Label>
                          {editable ? (
                            <Switch
                              id={`dir-app-${system.id}-${slug}`}
                              checked={appOn}
                              onCheckedChange={(checked) => setApp(slug, checked === true)}
                            />
                          ) : (
                            <span className={`text-xs ${appOn ? "text-emerald-700 dark:text-emerald-300" : "text-gray-400"}`}>
                              {appOn ? "Yes" : "No"}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ) : null}

            {on && system.innerNote ? (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{system.innerNote}</p>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
